import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom/cjs/react-router-dom.min";
//
import styled from "styled-components";
import {
  Calendar,
  Compass,
  Home as HomeIcon,
  Settings,
  Plus,
} from "lucide-react";
import { FaPenNib } from "react-icons/fa";

// Custom fountain pen icon component for mobile
const FountainPenIcon = (props) => {
  return <FaPenNib style={props.style} />;
};

const NAV_ITEMS = [
  { to: "/", label: "Home", Icon: HomeIcon },
  { to: "/explore", label: "Explore", Icon: Compass },
  { to: "/create", label: "Create", Icon: FountainPenIcon, isCenter: true },
  { to: "/events", label: "Events", Icon: Calendar },
  { to: "/settings", label: "Settings", Icon: Settings },
];

const BottomNav = () => {
  const location = useLocation();
  const [visible, setVisible] = useState(true);
  const lastY = useRef(typeof window !== "undefined" ? getScrollPosition() : 0);
  const listeners = useRef([]);

  function getScrollPosition() {
    const doc = document.documentElement;
    const body = document.body;
    return Math.max(doc?.scrollTop || 0, body?.scrollTop || 0);
  }

  useEffect(() => {
    if (typeof window === "undefined") return;

    let ticking = false;

    const onScroll = () => {
      if (ticking) return;
      ticking = true;

      window.requestAnimationFrame(() => {
        const current = getScrollPosition();
        const delta = current - lastY.current;
        const scrolledUp = delta < -4;
        const scrolledDown = delta > 4;

        if (current < 50 || scrolledUp) {
          setVisible(true);
        } else if (scrolledDown) {
          setVisible(false);
        }

        lastY.current = current;
        ticking = false;
      });
    };

    const targets = [
      window,
      document,
      document.documentElement,
      document.body,
      document.getElementById("root"),
    ].filter(Boolean);

    targets.forEach((t) => {
      t.addEventListener("scroll", onScroll, { passive: true });
      listeners.current.push([t, onScroll]);
    });

    return () => {
      listeners.current.forEach(([t, handler]) =>
        t.removeEventListener("scroll", handler),
      );
      listeners.current = [];
    };
  }, []);

  const isActive = (path) => {
    if (path === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(path);
  };

  return (
    <Bar $visible={visible} data-bottom-nav-test>
      {NAV_ITEMS.map(({ to, label, Icon, isCenter }) => {
        const active = isActive(to);
        return (
          <NavItem key={to} $active={active} $center={isCenter}>
            <StyledLink to={to} aria-label={label} $center={isCenter}>
              <IconWrapper $active={active} $center={isCenter}>
                <Icon />
              </IconWrapper>
              <Label $active={active}>{label}</Label>
            </StyledLink>
          </NavItem>
        );
      })}
    </Bar>
  );
};

const Bar = styled.nav`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  align-items: center;
  padding: 6px 8px 12px 8px;
  background: var(--color-background, #ffffff);
  border-top: 1px solid var(--color-border, #d9d9d9);
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.05);
  transition:
    transform 0.25s ease,
    opacity 0.2s ease;
  transform: ${({ $visible }) =>
    $visible ? "translateY(0)" : "translateY(110%)"};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  pointer-events: ${({ $visible }) => ($visible ? "auto" : "none")};

  @media (min-width: 768px) {
    display: none;
  }
`;

const NavItem = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  transform: ${({ $center }) => ($center ? "translateY(-8px)" : "none")};
`;

const StyledLink = styled(Link)`
  text-decoration: none;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: ${({ $center }) => ($center ? "70px" : "100%")};
`;

const IconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${({ $center }) => ($center ? "48px" : "30px")};
  height: ${({ $center }) => ($center ? "48px" : "30px")};
  border-radius: ${({ $center }) => ($center ? "50%" : "12px")};
  border: ${({ $center }) =>
    $center ? "3px solid var(--color-text-inverse)" : "none"};
  color: ${({ $center, $active }) =>
    $center
      ? "var(--color-text-inverse)"
      : $active
        ? "var(--color-primary)"
        : "var(--color-text-secondary)"};
  background: ${({ $center }) =>
    $center ? "var(--color-primary)" : "transparent"};
  box-shadow: ${({ $center }) =>
    $center ? "0 6px 14px rgba(31, 107, 80, 0.35)" : "none"};

  svg {
    width: ${({ $center }) => ($center ? "24px" : "20px")};
    height: ${({ $center }) => ($center ? "24px" : "20px")};
    color: ${({ $center, $active }) =>
      $center
        ? "var(--color-text-inverse)"
        : $active
          ? "var(--color-primary)"
          : "var(--color-text-secondary)"};
  }
`;

const Label = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: ${({ $active }) => ($active ? "#1f6b50" : "#7a7f85")};
`;

export default BottomNav;
